import { Input, Modal, Upload } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import imageCompression from 'browser-image-compression';
import { uploadMediaRequest } from '../../../../../lib/store/slices/findersSlice';
import { useBeforeUploadError } from '../utils/hooks/useBeforeUploadError';
import { createFormData, getBase64 } from '../../../../../lib/util/helpers';
import { appLogger } from '../../../../../lib/util/logger';
import { useState } from 'react';

// eslint-disable-next-line max-lines-per-function
export function FinderFileLoader({
  uploadLabel,
  afterUploadAction,
  accept = 'image/*',
  maxFileSize,
  errorText,
  targetType = 'FINDER',
  disabled,
  setUploading,
}) {
  const dispatch = useDispatch();
  const { finder, tenant, isViewMode } = useSelector(({ finders }) => finders);
  const { beforeUpload, contextHolder } = useBeforeUploadError(maxFileSize, errorText);

  const [altText, setAltText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [formDataState, setFormDataState] = useState(null);

  const customUploadRequest = async ({ file }) => {
    try {
      let fileForUpload = file;

      if (accept.includes('image') && !file.type.includes('svg')) {
        const preview = await getBase64(file);
        setPreviewImage(preview);
        setIsModalVisible(true);

        fileForUpload = await imageCompression(file, { maxSizeMB: 10 });
      } else {
        const formData = createFormData({
          file: fileForUpload,
          targetType,
          targetId: finder.id,
          originalFileName: file.name,
        });

        dispatch(
          uploadMediaRequest({
            formData,
            alternativeText: altText,
            afterUploadAction,
            tenantId: tenant.id,
            setUploading,
          })
        );
      }

      setFormDataState(fileForUpload);
      setUploading?.(true);
    } catch (error) {
      appLogger.error(error);
      setUploading?.(false);
    }
  };

  const handleOk = () => {
    if (!formDataState) return;

    const formData = createFormData({
      file: formDataState,
      targetType,
      targetId: finder.id,
      originalFileName: formDataState.name,
    });

    dispatch(
      uploadMediaRequest({
        formData,
        alternativeText: altText,
        afterUploadAction,
        tenantId: tenant.id,
        setUploading,
      })
    );

    setIsModalVisible(false);
    setPreviewImage(null);
    setAltText('');
    setFormDataState(null);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewImage(null);
    setAltText('');
    setFormDataState(null);
    setUploading?.(false);
  };

  return (
    <>
      {contextHolder}
      <Upload
        accept={accept}
        beforeUpload={beforeUpload}
        customRequest={customUploadRequest}
        showUploadList={false}
        disabled={disabled || isViewMode}
      >
        {uploadLabel}
      </Upload>
      <Modal
        title='Image Preview'
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText='Save'
        cancelText='Cancel'
        footer={(__, { OkBtn, CancelBtn }) => (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
            <CancelBtn />
            <OkBtn />
          </div>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {previewImage && (
            <img
              src={previewImage}
              alt='Preview'
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                marginBottom: 16,
              }}
            />
          )}
          <Input placeholder='Enter alternative text' value={altText} onChange={event => setAltText(event.target.value)} required />
        </div>
      </Modal>
    </>
  );
}
